<script setup lang="ts">
import "./ChangeWorkspaceDialog.scss";
import { inject, provide, ref } from "vue";
import { NOOP } from "@vue/shared";
import { SIMPLE_THRESHOLD_DEFAULT, useChangeWorkspace } from "./composables/useChangeWorkspace";

const props = withDefaults(defineProps<{ simpleThreshold?: number }>(), {
  simpleThreshold: SIMPLE_THRESHOLD_DEFAULT,
});

const isMacOS = navigator.userAgent.includes("Macintosh");

const close = inject<() => void>("close", NOOP);
const setBgGrey = inject<(v: boolean) => void>("setBgGrey", NOOP);
const removePadding = inject<(v: boolean) => void>("removePadding", NOOP);

setBgGrey(true);
removePadding(true);

// ENG-21322: prevent m-input from marking the drawer as having unsaved changes
provide("formTouched", NOOP);

const contentRef = ref<HTMLElement | null>(null);

const {
  searchQuery,
  currentKey,
  currentTenant,
  isSimple,
  allTenants,
  recent,
  allOther,
  filteredTenants,
  showSearchResults,
  getHighlightParts,
  itemTabindex,
  setActive,
  switchTo,
  onSearchKeydown,
  onItemKeydown,
} = useChangeWorkspace(close, contentRef, props.simpleThreshold);
</script>

<template>
  <div
    ref="contentRef"
    :class="['change-workspace-content', { 'change-workspace-content--fixed': !isSimple }]"
  >
    <div v-if="!isSimple" class="change-workspace-search">
      <input
        v-model="searchQuery"
        class="change-workspace-search-input"
        type="search"
        placeholder="Search workspaces…"
        aria-label="Search workspaces"
        autocomplete="off"
        @keydown="onSearchKeydown"
      />
    </div>

    <div class="change-workspace-list" tabindex="-1">
      <template v-if="showSearchResults">
        <div class="change-workspace-results-header">
          <span>Results</span>
          <span
            >{{ filteredTenants.length }} result{{ filteredTenants.length === 1 ? "" : "s" }}</span
          >
        </div>
        <p v-if="filteredTenants.length === 0" class="change-workspace-empty">
          No workspaces found
        </p>
        <div v-else role="listbox" aria-label="Search results">
          <button
            v-for="tenant in filteredTenants"
            :key="tenant.key"
            class="change-workspace-item"
            :class="{ 'is-current': tenant.key === currentKey }"
            role="option"
            :aria-selected="tenant.key === currentKey"
            :tabindex="itemTabindex(tenant.key)"
            @click="switchTo(tenant.key)"
            @focus="setActive(tenant.key)"
            @keydown="onItemKeydown"
          >
            <span class="change-workspace-item-name">
              <template v-for="(part, pi) in getHighlightParts(tenant.name)" :key="pi">
                <mark v-if="part.hi">{{ part.t }}</mark>
                <template v-else>{{ part.t }}</template>
              </template>
            </span>
            <span class="change-workspace-item-icon">
              <m-icon v-if="tenant.key === currentKey" name="fa-solid fa-check" />
            </span>
          </button>
        </div>
      </template>

      <!-- ENG-21322: simple mode (≤ threshold) — Current at top, flat list, no sections -->
      <template v-else-if="isSimple">
        <div v-if="currentTenant">
          <p class="change-workspace-section-title">Current</p>
          <button
            class="change-workspace-item is-current"
            :tabindex="itemTabindex(currentKey)"
            @click="switchTo(currentKey)"
            @focus="setActive(currentKey)"
            @keydown="onItemKeydown"
          >
            <span class="change-workspace-item-name">{{ currentTenant.name }}</span>
            <span class="change-workspace-item-icon"><m-icon name="fa-solid fa-check" /></span>
          </button>
        </div>
        <div>
          <p class="change-workspace-section-title">Other Workspaces</p>
          <button
            v-for="tenant in allTenants.filter((t) => t.key !== currentKey)"
            :key="tenant.key"
            class="change-workspace-item"
            :tabindex="itemTabindex(tenant.key)"
            @click="switchTo(tenant.key)"
            @focus="setActive(tenant.key)"
            @keydown="onItemKeydown"
          >
            <span class="change-workspace-item-name">{{ tenant.name }}</span>
          </button>
        </div>
      </template>

      <!-- ENG-21322: full mode (> threshold) — Current + Recent + Other Workspaces sections -->
      <template v-else>
        <div v-if="currentTenant">
          <p class="change-workspace-section-title">Current</p>
          <button
            class="change-workspace-item is-current"
            :tabindex="itemTabindex(currentKey)"
            @click="switchTo(currentKey)"
            @focus="setActive(currentKey)"
            @keydown="onItemKeydown"
          >
            <span class="change-workspace-item-name">{{ currentTenant.name }}</span>
            <span class="change-workspace-item-icon">
              <m-icon name="fa-solid fa-check" />
            </span>
          </button>
        </div>

        <div v-if="recent.length > 0">
          <p class="change-workspace-section-title">Recent</p>
          <button
            v-for="tenant in recent"
            :key="tenant.key"
            class="change-workspace-item"
            :tabindex="itemTabindex(tenant.key)"
            @click="switchTo(tenant.key)"
            @focus="setActive(tenant.key)"
            @keydown="onItemKeydown"
          >
            <span class="change-workspace-item-name">{{ tenant.name }}</span>
          </button>
        </div>

        <div v-if="allOther.length > 0">
          <p class="change-workspace-section-title">
            <span>Other Workspaces</span>
            <span>{{ allOther.length }}</span>
          </p>
          <button
            v-for="tenant in allOther"
            :key="tenant.key"
            class="change-workspace-item"
            :tabindex="itemTabindex(tenant.key)"
            @click="switchTo(tenant.key)"
            @focus="setActive(tenant.key)"
            @keydown="onItemKeydown"
          >
            <span class="change-workspace-item-name">{{ tenant.name }}</span>
          </button>
        </div>
      </template>
    </div>

    <div
      class="change-workspace-footer"
      :aria-label="
        isMacOS
          ? 'Keyboard shortcut: Option W to open or close'
          : 'Keyboard shortcut: Alt W to open or close'
      "
    >
      <span class="change-workspace-footer-shortcut">
        <kbd>{{ isMacOS ? "⌥ option" : "Alt" }}</kbd
        ><kbd>W</kbd>
      </span>
      <span>to open / close</span>
    </div>
  </div>
</template>
